import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline, Polygon as LeafletPolygon, useMapEvents } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import axios from 'axios';
import * as XLSX from 'xlsx';
import './MonitoramentoVolkswagenModule.css';
import PoligonoEditor from './PoligonoEditor';

// Fix para ícones do Leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

interface Veiculo {
  id: number;
  placa: string;
  motorista: string;
  responsavel?: string;
  status: string;
  velocidade: number;
  localizacao?: { lat: number; lng: number; endereco?: string };
  ultimaAtualizacao?: string;
  ultimaComunicacao?: string;
  tempoParado?: number;
  operacao?: string;
  poligono?: string;
  sentidoRota?: 'ida' | 'volta' | 'desconhecido';
  confiancaSentido?: number;
  problemaRastreador?: boolean;
  horarioSaidaOrigem?: string;
}

interface VeiculoTimeline {
  placa: string;
  motorista: string;
  distanciaRestante: number;
  carrocerias: number;
  velocidadeMedia: number;
  tempoEstimado: string;
  horaChegada: string;
  ultimaAtualizacao: string;
  poligono?: string;
  statusVeiculo?: string;
  horarioSaidaOrigem?: string;
  cva?: string;
  motoristaNucci?: string;
}

interface Estatisticas {
  totalVeiculos: number;
  emMovimento: number;
  parados: number;
  foraDeRota: number;
  problemaRastreador: number;
  ultimaAtualizacao: string;
}

interface Rota {
  id: number;
  nome: string;
  origem: string;
  destino: string;
  pontosParada?: string;
  pontos: Array<{ lat: number; lng: number }>;
  distancia: number;
  tempoEstimado: number;
  cor: string;
}

interface Operacao {
  id?: number;
  nome: string;
  descricao?: string;
  rotas: number[];
  veiculos: string[]; // Changed from number[] to string[] to store placas
  poligonoOrigem?: number;
  poligonoDestino?: number;
  ativa: boolean;
}

interface Poligono {
  id?: number;
  nome: string;
  tipo: 'patio' | 'cliente' | 'posto' | 'oficina' | 'outro';
  descricao?: string;
  coordenadas: Array<{ lat: number; lng: number }>;
  cor: string;
  ativa?: boolean;
}

const MonitoramentoVolkswagenModule: React.FC = () => {
  const [veiculos, setVeiculos] = useState<Veiculo[]>([]);
  const [rotas, setRotas] = useState<Rota[]>([]);
  const [estatisticas, setEstatisticas] = useState<Estatisticas>({
    totalVeiculos: 0,
    emMovimento: 0,
    parados: 0,
    foraDeRota: 0,
    problemaRastreador: 0,
    ultimaAtualizacao: new Date().toLocaleString('pt-BR')
  });
  const [veiculoSelecionado, setVeiculoSelecionado] = useState<Veiculo | null>(null);
  const [filtroStatus, setFiltroStatus] = useState<string>('todos');
  const [filtroSentido, setFiltroSentido] = useState<string>('todos');
  const [filtroResponsavel, setFiltroResponsavel] = useState<string[]>([]);
  const [filtroOperacao, setFiltroOperacao] = useState<number | null>(null);
  const [filtroPlaca, setFiltroPlaca] = useState<string>('');
  const [showCadastroRota, setShowCadastroRota] = useState(false);
  const [rotaEmEdicao, setRotaEmEdicao] = useState<number | null>(null);
  const [showRotas, setShowRotas] = useState(false);
  const [rotasSelecionadas, setRotasSelecionadas] = useState<number[]>([]);
  const [mapaRef, setMapaRef] = useState<any>(null);
  const markerRefs = React.useRef<{ [key: number]: any }>({});
  const mapContainerRef = React.useRef<any>(null);
  const [novaRota, setNovaRota] = useState({
    nome: '',
    origem: '',
    destino: '',
    pontosParada: '',
    distancia: '',
    tempoEstimado: '',
    cor: '#2196f3',
    pontos: [{ lat: '', lng: '', endereco: '' }], // agora é array de objetos
  });
  const [rotaSelecionada, setRotaSelecionada] = useState<Rota | null>(null);
  const [rotasDetalhadas, setRotasDetalhadas] = useState<{ [key: number]: Array<[number, number]> }>({});
  const [operacoes, setOperacoes] = useState<Operacao[]>([]);
  const [showCadastroOperacao, setShowCadastroOperacao] = useState(false);
  const [showListaOperacoes, setShowListaOperacoes] = useState(false);
  const [operacaoEmEdicao, setOperacaoEmEdicao] = useState<number | null>(null);
  const [filtroRotasModal, setFiltroRotasModal] = useState<string>('');
  const [filtroVeiculosModal, setFiltroVeiculosModal] = useState<string>('');
  const [novaOperacao, setNovaOperacao] = useState<Operacao>({
    nome: '',
    descricao: '',
    rotas: [],
    veiculos: [],
    poligonoOrigem: undefined,
    poligonoDestino: undefined,
    ativa: true
  });
  const [poligonos, setPoligonos] = useState<Poligono[]>([]);
  const [dadosNucci, setDadosNucci] = useState<any>({});
  const [showTooltipProblemaRastreador, setShowTooltipProblemaRastreador] = useState(false);
  const [showTooltipEmMovimento, setShowTooltipEmMovimento] = useState(false);
  const [showTooltipParados, setShowTooltipParados] = useState(false);
  const [showTooltipForaRota, setShowTooltipForaRota] = useState(false);
  const [showTooltipForaRotaCard, setShowTooltipForaRotaCard] = useState(false);
  const [showTooltipProblemaRastreadorCard, setShowTooltipProblemaRastreadorCard] = useState(false);
  const [showModalVeiculos, setShowModalVeiculos] = useState(false);

  // Coordenadas das plantas VW
  const VW_ANCHIETA = { lat: -23.8779, lng: -46.4056 }; // São Bernardo do Campo
  const VW_AUDI_SJP = { lat: -25.5892, lng: -49.1728 }; // São José dos Pinhais

  // Função para calcular distância entre dois pontos (Haversine formula)
  const calcularDistancia = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
    const R = 6371; // Raio da Terra em km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  };

  // Função para formatar tempo estimado
  const formatarTempoEstimado = (distanciaKm: number, velocidadeMedia: number): string => {
    if (velocidadeMedia === 0) return 'N/A';
    const horas = distanciaKm / velocidadeMedia;
    const horasInt = Math.floor(horas);
    const minutos = Math.round((horas - horasInt) * 60);
    return `${horasInt}h ${minutos}min`;
  };

  // Função para calcular hora de chegada estimada
  const calcularHoraChegada = (distanciaKm: number, velocidadeMedia: number): string => {
    if (velocidadeMedia === 0) return 'N/A';
    const horasRestantes = distanciaKm / velocidadeMedia;
    const agora = new Date();
    const chegada = new Date(agora.getTime() + horasRestantes * 60 * 60 * 1000);
    const horas = chegada.getHours().toString().padStart(2, '0');
    const minutos = chegada.getMinutes().toString().padStart(2, '0');
    return `${horas}:${minutos}`;
  };

  // Função para gerar relatório Excel de veículos com problema no rastreador
  const gerarRelatorioProblemaRastreador = () => {
    const veiculosComProblema = veiculosFiltrados.filter(v => v.problemaRastreador);
    
    if (veiculosComProblema.length === 0) {
      alert('Não há veículos com problema no rastreador no momento.');
      return;
    }

    // Preparar dados para o Excel
    const dadosExcel = veiculosComProblema.map(v => ({
      'Placa': v.placa,
      'Responsável': v.responsavel || 'Não informado',
      'Última Comunicação': v.ultimaComunicacao || 'Não disponível'
    }));

    // Criar workbook e worksheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(dadosExcel);

    // Ajustar largura das colunas
    ws['!cols'] = [
      { wch: 12 }, // Placa
      { wch: 30 }, // Responsável
      { wch: 20 }  // Última Comunicação
    ];

    // Adicionar worksheet ao workbook
    XLSX.utils.book_append_sheet(wb, ws, 'Problemas Rastreador');

    // Gerar arquivo e fazer download
    const dataAtual = new Date().toLocaleDateString('pt-BR').replace(/\//g, '-');
    XLSX.writeFile(wb, `Relatorio_Problema_Rastreador_${dataAtual}.xlsx`);
  };

  // Função para gerar relatório Excel de veículos em movimento
  const gerarRelatorioEmMovimento = () => {
    const veiculosEmMovimento = veiculosFiltrados.filter(v => {
      if (v.status !== 'em_movimento' || v.problemaRastreador) return false;
      // Se velocidade > 10 e não está em polígono, está em movimento
      if (v.velocidade > 10 && (!v.poligono || v.poligono === '-')) return true;
      // Se velocidade <= 10 mas não está em polígono (pode estar no trânsito), considera em movimento
      if (v.velocidade <= 10 && (!v.poligono || v.poligono === '-')) return true;
      // Se está em algum polígono, não considera em movimento
      return false;
    });
    
    if (veiculosEmMovimento.length === 0) {
      alert('Não há veículos em movimento no momento.');
      return;
    }

    const dadosExcel = veiculosEmMovimento.map(v => ({
      'Placa': v.placa,
      'Motorista': v.motorista || 'Não informado',
      'Responsável': v.responsavel || 'Não informado',
      'Velocidade': `${v.velocidade} km/h`,
      'Localização': v.localizacao?.endereco || 'Não disponível'
    }));

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(dadosExcel);
    ws['!cols'] = [{ wch: 12 }, { wch: 25 }, { wch: 25 }, { wch: 12 }, { wch: 40 }];
    XLSX.utils.book_append_sheet(wb, ws, 'Veículos em Movimento');

    const dataAtual = new Date().toLocaleDateString('pt-BR').replace(/\//g, '-');
    XLSX.writeFile(wb, `Relatorio_Em_Movimento_${dataAtual}.xlsx`);
  };

  // Função para gerar relatório Excel de veículos parados
  const gerarRelatorioParados = () => {
    const veiculosParados = veiculosFiltrados.filter(v => v.status === 'parado' && !v.problemaRastreador);
    
    if (veiculosParados.length === 0) {
      alert('Não há veículos parados no momento.');
      return;
    }

    const dadosExcel = veiculosParados.map(v => ({
      'Placa': v.placa,
      'Motorista': v.motorista || 'Não informado',
      'Responsável': v.responsavel || 'Não informado',
      'Tempo Parado (min)': v.tempoParado,
      'Localização (Polígono)': v.poligono || 'Sem informação',
      'Endereço': v.localizacao?.endereco || 'Não disponível'
    }));

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(dadosExcel);
    ws['!cols'] = [{ wch: 12 }, { wch: 25 }, { wch: 25 }, { wch: 15 }, { wch: 30 }, { wch: 40 }];
    XLSX.utils.book_append_sheet(wb, ws, 'Veículos Parados');

    const dataAtual = new Date().toLocaleDateString('pt-BR').replace(/\//g, '-');
    XLSX.writeFile(wb, `Relatorio_Parados_${dataAtual}.xlsx`);
  };

  // Função para gerar relatório Excel de veículos fora de rota
  const gerarRelatorioForaRota = () => {
    const veiculosForaRota = veiculosFiltrados.filter(v => v.status === 'fora_de_rota' && !v.problemaRastreador);
    
    if (veiculosForaRota.length === 0) {
      alert('Não há veículos fora de rota no momento.');
      return;
    }

    const dadosExcel = veiculosForaRota.map(v => ({
      'Placa': v.placa,
      'Motorista': v.motorista || 'Não informado',
      'Responsável': v.responsavel || 'Não informado',
      'Operação': v.operacao || 'Não informado',
      'Localização': v.localizacao?.endereco || 'Não disponível'
    }));

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(dadosExcel);
    ws['!cols'] = [{ wch: 12 }, { wch: 25 }, { wch: 25 }, { wch: 20 }, { wch: 40 }];
    XLSX.utils.book_append_sheet(wb, ws, 'Veículos Fora de Rota');

    const dataAtual = new Date().toLocaleDateString('pt-BR').replace(/\//g, '-');
    XLSX.writeFile(wb, `Relatorio_Fora_Rota_${dataAtual}.xlsx`);
  };

  // Buscar dados reais da API
  useEffect(() => {
    fetchDados();
    
    // Atualizar a cada 30 segundos
    const interval = setInterval(fetchDados, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchDados = async () => {
    try {
      // Criar um mapa dos dados da Nucci anteriores para manter durante a atualização
      const dadosNucciAnteriores = { ...dadosNucci };
      
      // Buscar veículos
      const resVeiculos = await axios.get('/api/monitoramento/veiculos');
      const dadosVeiculos = resVeiculos.data.map((v: any) => ({
        id: v.id,
        placa: v.placa,
        motorista: v.motorista || 'Não informado',
        responsavel: v.responsavel || '',
        status: v.status || 'parado',
        velocidade: v.velocidade || 0,
        localizacao: v.localizacao || { lat: -23.550520, lng: -46.633308, endereco: 'Localização não disponível' },
        ultimaAtualizacao: v.ultimaAtualizacao ? new Date(v.ultimaAtualizacao).toLocaleTimeString('pt-BR') : '-',
        ultimaComunicacao: v.ultimaComunicacao ? new Date(v.ultimaComunicacao).toLocaleString('pt-BR') : '-',
        tempoParado: v.tempoParado || 0,
        operacao: v.operacao || '-',
        poligono: v.poligono || '-',
        sentidoRota: v.sentidoRota || 'desconhecido',
        confiancaSentido: v.confiancaSentido || 0,
        problemaRastreador: v.problemaRastreador || false
      }));
      setVeiculos(dadosVeiculos);

      // Buscar estatísticas
      const resStats = await axios.get('/api/monitoramento/estatisticas');
      setEstatisticas({
        ...resStats.data,
        ultimaAtualizacao: new Date().toLocaleString('pt-BR')
      });

      // Buscar rotas
      const resRotas = await axios.get('/api/monitoramento/rotas');
      setRotas(resRotas.data);

      // Buscar operações
      const resOperacoes = await axios.get('/api/monitoramento/operacoes');
      setOperacoes(resOperacoes.data);

      // Buscar polígonos
      const resPoligonos = await axios.get('/api/monitoramento/poligonos');
      setPoligonos(resPoligonos.data);

      // Buscar dados da Nucci (CVA e motorista) para veículos da operação Volks - EM PARALELO
      if (dadosVeiculos.length > 0) {
        const placasVolks = dadosVeiculos.map((v: any) => v.placa).join(',');
        // Usar dados anteriores temporariamente
        setDadosNucci(dadosNucciAnteriores);
        
        // Buscar novos dados em paralelo
        axios.get(`/api/monitoramento/volkswagen/dados-nucci?placas=${placasVolks}`)
          .then(resDadosNucci => {
            const novosDadosNucci = resDadosNucci.data;
            // Fazer merge com dados anteriores (manter dados que não foram atualizados)
            const dadosMerged = { ...dadosNucciAnteriores };
            Object.keys(novosDadosNucci).forEach(placa => {
              if (novosDadosNucci[placa] && novosDadosNucci[placa].cva) {
                dadosMerged[placa] = novosDadosNucci[placa];
              }
            });
            setDadosNucci(dadosMerged);
          })
          .catch(error => {
            console.error('Erro ao buscar dados da Nucci:', error);
            // Manter dados anteriores em caso de erro
            setDadosNucci(dadosNucciAnteriores);
          });
      }
    } catch (error) {
      console.error('Erro ao buscar dados:', error);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'em_movimento': return '#4caf50';
      case 'parado': return '#ff9800';
      case 'fora_de_rota': return '#f44336';
      default: return '#9e9e9e';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'em_movimento': return 'Em Movimento';
      case 'parado': return 'Parado';
      case 'fora_de_rota': return 'Fora de Rota';
      default: return 'Desconhecido';
    }
  };

  // Extrai lista de responsáveis únicos dos veículos
  const responsaveis = Array.from(new Set(veiculos.map(v => v.responsavel && v.responsavel.trim() ? v.responsavel.trim() : 'Não informado')));

  // Obtém operação selecionada
  // Buscar operação Volks automaticamente
  const operacaoVolks = operacoes.find(op => op.nome && op.nome.toLowerCase().includes('volks'));
  const operacaoSelecionada = operacaoVolks;

  // Filtra veículos apenas por operação Volks e placa
  const veiculosFiltrados = veiculos.filter(v => {
    // Filtrar apenas veículos da operação Volks
    const operacaoOk = !operacaoVolks || operacaoVolks.veiculos.includes(v.placa);
    const placaOk = !filtroPlaca || v.placa.toUpperCase().includes(filtroPlaca.toUpperCase());
    return operacaoOk && placaOk;
  });

  // Atualiza rotas selecionadas automaticamente para operação Volks (exibir no mapa)
  React.useEffect(() => {
    if (operacaoSelecionada) {
      setRotasSelecionadas(operacaoSelecionada.rotas);
    }
  }, [operacaoSelecionada]);

  // Função para alternar seleção de responsável
  const toggleResponsavel = (responsavel: string) => {
    setFiltroResponsavel(prev => 
      prev.includes(responsavel) 
        ? prev.filter(r => r !== responsavel)
        : [...prev, responsavel]
    );
  };

  // Indicadores filtrados (apenas Em Movimento e Parados)
  // Em movimento: velocidade > 10 OU (velocidade <= 10 E não está em polígono)
  const veiculosEmMovimento = veiculosFiltrados.filter(v => {
    if (v.status !== 'em_movimento' || v.problemaRastreador) return false;
    // Se velocidade > 10 e não está em polígono, está em movimento
    if (v.velocidade > 10 && (!v.poligono || v.poligono === '-')) return true;
    // Se velocidade <= 10 mas não está em polígono (pode estar no trânsito), considera em movimento
    if (v.velocidade <= 10 && (!v.poligono || v.poligono === '-')) return true;
    // Se está em algum polígono, não considera em movimento
    return false;
  }).length;
  const veiculosParados = veiculosFiltrados.filter(v => v.status === 'parado' && !v.problemaRastreador).length;
  
  const estatisticasFiltradas = {
    totalVeiculos: veiculosEmMovimento + veiculosParados,
    emMovimento: veiculosEmMovimento,
    parados: veiculosParados,
  };

  // Calcular timeline de veículos usando useMemo para evitar loops infinitos
  const veiculosTimeline = React.useMemo(() => {
    const veiculosEmRota = veiculosFiltrados.filter(v => {
      // Incluir veículos com sentido "ida" (indo para SJP)
      if (v.problemaRastreador || v.sentidoRota !== 'ida' || !v.localizacao) {
        return false;
      }
      
      // Incluir veículos EM MOVIMENTO fora de polígonos
      if (v.status === 'em_movimento' && (!v.poligono || v.poligono === '-')) {
        return true;
      }
      
      // Incluir veículos PARADOS, mas EXCLUIR se estiverem em polígonos de pátio ou cliente
      if (v.status === 'parado') {
        // Se não tem polígono ou polígono é "-", inclui
        if (!v.poligono || v.poligono === '-') return true;
        
        // Se está em um polígono, verifica o tipo
        const poligonoAtual = poligonos.find(p => p.nome === v.poligono);
        // Exclui se for pátio ou cliente
        if (poligonoAtual && (poligonoAtual.tipo === 'patio' || poligonoAtual.tipo === 'cliente')) {
          return false;
        }
        // Inclui se for outro tipo (posto, oficina, outro)
        return true;
      }
      
      return false;
    });

    const timelineData: VeiculoTimeline[] = veiculosEmRota.map(v => {
      const distancia = v.localizacao 
        ? calcularDistancia(v.localizacao.lat, v.localizacao.lng, VW_AUDI_SJP.lat, VW_AUDI_SJP.lng)
        : 0;
      const velocidade = v.velocidade || 60;
      
      // Buscar dados da Nucci para esta placa
      const dadosPlacaNucci = dadosNucci[v.placa];
      
      return {
        placa: v.placa,
        motorista: v.motorista,
        distanciaRestante: Math.round(distancia),
        carrocerias: 5, // Cada veículo carrega 5 carrocerias
        velocidadeMedia: velocidade,
        tempoEstimado: formatarTempoEstimado(distancia, velocidade),
        horaChegada: calcularHoraChegada(distancia, velocidade),
        ultimaAtualizacao: v.ultimaAtualizacao || '-',
        poligono: v.poligono && v.poligono !== '-' ? v.poligono : undefined,
        statusVeiculo: v.status,
        horarioSaidaOrigem: v.horarioSaidaOrigem,
        cva: dadosPlacaNucci?.cva,
        motoristaNucci: dadosPlacaNucci?.motoristaNucci
      };
    });

    // Ordenar por tempo estimado (menor tempo primeiro)
    timelineData.sort((a, b) => {
      const tempoA = a.distanciaRestante / a.velocidadeMedia;
      const tempoB = b.distanciaRestante / b.velocidadeMedia;
      return tempoA - tempoB;
    });

    return timelineData;
  }, [veiculosFiltrados, poligonos, dadosNucci]);

  // Calcular carrocerias acumuladas por intervalo usando useMemo
  const carroceriasAcumuladas = React.useMemo(() => {
    const intervalos = [
      { inicio: 0, fim: 30, label: '30 min' },
      { inicio: 30, fim: 60, label: '60 min (1h)' },
      { inicio: 60, fim: 90, label: '90 min (1h30)' },
      { inicio: 90, fim: 120, label: '120 min (2h)' },
      { inicio: 120, fim: 150, label: '150 min (2h30)' },
      { inicio: 150, fim: 180, label: '180 min (3h)' },
      { inicio: 180, fim: Infinity, label: '+180 min (+3h)' }
    ];
    
    return intervalos.map(intervalo => {
      const carroceriasNoIntervalo = veiculosTimeline.filter(v => {
        const tempoRestanteHoras = v.distanciaRestante / (v.velocidadeMedia || 60);
        const tempoRestanteMinutos = tempoRestanteHoras * 60;
        return tempoRestanteMinutos > intervalo.inicio && tempoRestanteMinutos <= intervalo.fim;
      }).reduce((sum, v) => sum + v.carrocerias, 0);

      return {
        tempo: intervalo.label,
        quantidade: carroceriasNoIntervalo
      };
    });
  }, [veiculosTimeline]);

  // Abrir popup do marcador selecionado quando veiculoSelecionado mudar
  useEffect(() => {
    if (veiculoSelecionado && markerRefs.current[veiculoSelecionado.id]) {
      const marker = markerRefs.current[veiculoSelecionado.id];
      if (marker && marker.openPopup) {
        marker.openPopup();
      }
      if (mapContainerRef.current && mapContainerRef.current.setView && veiculoSelecionado.localizacao) {
        mapContainerRef.current.setView([veiculoSelecionado.localizacao.lat, veiculoSelecionado.localizacao.lng], 16);
      }
    }
  }, [veiculoSelecionado]);

  const toggleRota = (rotaId: number) => {
    setRotasSelecionadas(prev => 
      prev.includes(rotaId) 
        ? prev.filter(id => id !== rotaId)
        : [...prev, rotaId]
    );
  };

  // Ícone de caminhão customizado com cabine colorida
  const createCustomIcon = (veiculo: Veiculo) => {
    // Se tem problema no rastreador, usa cor roxa
    const cabineColor = veiculo.problemaRastreador ? '#9c27b0' : getStatusColor(veiculo.status);
    const status = veiculo.problemaRastreador ? 'problema' : veiculo.status;
    
    // Determinar espelhamento baseado no sentido
    // Caminhão aponta para DIREITA por padrão (IDA)
    // Se for VOLTA, espelha horizontalmente para apontar para ESQUERDA
    const scaleX = veiculo.sentidoRota === 'volta' ? -1 : 1;
    
    return L.divIcon({
      className: 'custom-truck-marker',
      html: `
        <div style="transform: scaleX(${scaleX}); transform-origin: center; transition: transform 0.3s ease;">
          <svg width="36" height="36" viewBox="0 0 36 36" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <filter id="shadow-${status}" x="-50%" y="-50%" width="200%" height="200%">
                <feDropShadow dx="0" dy="1" stdDeviation="1.5" flood-opacity="0.3"/>
              </filter>
            </defs>
            <g filter="url(#shadow-${status})">
              <!-- Baú (container) -->
              <rect x="14" y="11" width="18" height="12" rx="2" fill="#4A90E2" stroke="#2C5F8D" stroke-width="1.2"/>
              <!-- Detalhe do baú -->
              <line x1="23" y1="11" x2="23" y2="23" stroke="#2C5F8D" stroke-width="0.8" opacity="0.5"/>
              
              <!-- Cabine -->
              <rect x="4" y="13" width="12" height="10" rx="2" fill="${cabineColor}" stroke="#1a1a1a" stroke-width="1.2"/>
              <!-- Janela da cabine -->
              <rect x="6.5" y="15" width="6" height="5" rx="1" fill="rgba(255,255,255,0.7)" stroke="#1a1a1a" stroke-width="0.6"/>
              
              <!-- Para-choque -->
              <rect x="4" y="23" width="12" height="2" rx="0.5" fill="#333"/>
              
              <!-- Rodas -->
              <circle cx="11" cy="28" r="3" fill="#2a2a2a" stroke="#444" stroke-width="1"/>
              <circle cx="11" cy="28" r="1.8" fill="#555"/>
              <circle cx="27" cy="28" r="3" fill="#2a2a2a" stroke="#444" stroke-width="1"/>
              <circle cx="27" cy="28" r="1.8" fill="#555"/>
            </g>
          </svg>
        </div>
      `,
      iconSize: [36, 36],
      iconAnchor: [18, 30],
      popupAnchor: [0, -30]
    });
  };

  const buscarLatLngPorEndereco = async (endereco: string) => {
    if (!endereco) return { lat: '', lng: '' };
    try {
      const res = await axios.get('/api/monitoramento/geocode-search', {
        params: { q: endereco }
      });
      const data = res.data?.results;
      if (data && data.length > 0) {
        return { lat: data[0].lat, lng: data[0].lon };
      }
    } catch (err) {}
    return { lat: '', lng: '' };
  };

  // Buscar trajeto detalhado para uma rota
  const buscarRotaDetalhada = async (rota: Rota) => {
    if (rota.pontos.length < 2) return;
    
    try {
      const coords = rota.pontos
        .filter(p => p.lat && p.lng)
        .map(p => `${Number(p.lng)},${Number(p.lat)}`)
        .join(';');
      if (!coords) {
        return;
      }
      
      const res = await fetch(`https://router.project-osrm.org/route/v1/driving/${coords}?overview=full&geometries=geojson`);
      const data = await res.json();
      
      if (data.routes && data.routes[0] && data.routes[0].geometry && data.routes[0].geometry.coordinates) {
        const polyline = data.routes[0].geometry.coordinates.map((coord: [number, number]) => [coord[1], coord[0]]);
        setRotasDetalhadas(prev => ({ ...prev, [rota.id]: polyline }));
        
        // Salvar pontos detalhados no backend para verificação precisa
        try {
          const pontosDetalhados = polyline.map(([lat, lng]: [number, number]) => ({ lat, lng }));
          await axios.put(`/api/monitoramento/rotas/${rota.id}/pontos-detalhados`, { pontosDetalhados });
        } catch (err: any) {
          // Erro ao salvar pontos detalhados - ignorar silenciosamente
        }
      }
    } catch (err) {
      // Erro ao buscar rota detalhada - ignorar silenciosamente
    }
  };

  // Quando rotas são marcadas/desmarcadas, buscar detalhes
  useEffect(() => {
    rotasSelecionadas.forEach(rotaId => {
      const rota = rotas.find(r => r.id === rotaId);
      if (rota && !rotasDetalhadas[rotaId]) {
        buscarRotaDetalhada(rota);
      }
    });
  }, [rotasSelecionadas, rotas]);

  const editarRota = (rota: Rota) => {
    setRotaEmEdicao(rota.id);
    setNovaRota({
      nome: rota.nome,
      origem: rota.origem,
      destino: rota.destino,
      pontosParada: rota.pontosParada || '',
      distancia: rota.distancia?.toString() || '',
      tempoEstimado: rota.tempoEstimado?.toString() || '',
      cor: rota.cor || '#2196f3',
      pontos: rota.pontos && rota.pontos.length > 0 ? rota.pontos.map(p => ({ lat: p.lat.toString(), lng: p.lng.toString(), endereco: '' })) : [{ lat: '', lng: '', endereco: '' }]
    });
    setShowCadastroRota(true);
  };

  const deletarRota = async (rotaId: number) => {
    if (!window.confirm('Tem certeza que deseja excluir esta rota?')) return;
    try {
      await axios.delete(`/api/monitoramento/rotas/${rotaId}`);
      fetchDados(); // Atualiza lista
      if (rotaSelecionada && rotaSelecionada.id === rotaId) setRotaSelecionada(null);
    } catch (err) {
      alert('Erro ao excluir rota!');
    }
  };

  // Funções de Polígonos
  const salvarPoligono = async (poligono: Poligono) => {
    try {
      await axios.post('/api/monitoramento/poligonos', poligono);
      await fetchDados();
      alert('Polígono cadastrado com sucesso!');
    } catch (err) {
      console.error('Erro ao cadastrar polígono:', err);
      alert('Erro ao cadastrar polígono!');
    }
  };

  const atualizarPoligono = async (id: number, poligono: Poligono) => {
    try {
      await axios.put(`/api/monitoramento/poligonos/${id}`, poligono);
      await fetchDados();
      alert('Polígono atualizado com sucesso!');
    } catch (err) {
      console.error('Erro ao atualizar polígono:', err);
      alert('Erro ao atualizar polígono!');
    }
  };

  const deletarPoligono = async (id: number) => {
    try {
      await axios.delete(`/api/monitoramento/poligonos/${id}`);
      await fetchDados();
      alert('Polígono removido com sucesso!');
    } catch (err) {
      console.error('Erro ao remover polígono:', err);
      alert('Erro ao remover polígono!');
    }
  };

  return (
    <div className="monitoramento-module">
      <div className="monitoramento-header">
        <h2>🚗 Monitoramento Volkswagen</h2>
        <div className="header-actions">
          <button className="btn-refresh" onClick={() => window.location.reload()}>
            🔄 Atualizar
          </button>
        </div>
      </div>

      {/* Filtro de placa e Cards Compactos */}
      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', marginBottom: 16, justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', gap: 16 }}>
          <div>
            <label style={{ fontWeight: 600, marginBottom: 4, display: 'block' }}>🏭 Operação:</label>
            <div style={{ 
              padding: '8px 12px', 
              border: '2px solid #1976d2', 
              borderRadius: 6, 
              fontSize: 14,
              minWidth: 200,
              background: '#e3f2fd',
              fontWeight: 600,
              color: '#1976d2'
            }}>
              {operacaoVolks ? operacaoVolks.nome : 'Volks (não encontrada)'}
            </div>
          </div>
          <div>
            <label style={{ fontWeight: 600, marginBottom: 4, display: 'block' }}>🔍 Buscar Placa:</label>
            <input 
              type="text"
              placeholder="Digite a placa..."
              value={filtroPlaca}
              onChange={e => setFiltroPlaca(e.target.value)}
              style={{ 
                padding: '8px 12px', 
                border: '2px solid #e0e0e0', 
                borderRadius: 6, 
                fontSize: 14,
                minWidth: 200,
                textTransform: 'uppercase'
              }}
            />
            {filtroPlaca && (
              <button 
                onClick={() => setFiltroPlaca('')}
                style={{ 
                  marginLeft: 8, 
                  padding: '6px 10px', 
                  border: 'none', 
                  background: '#f44336', 
                  color: 'white', 
                  borderRadius: 4, 
                  cursor: 'pointer',
                  fontSize: 12
                }}
              >
                ✕ Limpar
              </button>
            )}
          </div>
        </div>
        {/* Cards Compactos no Topo Direito */}
        <div style={{ display: 'flex', gap: '12px' }}>
          <div 
            onMouseEnter={() => setShowTooltipForaRotaCard(true)}
            onMouseLeave={() => setShowTooltipForaRotaCard(false)}
            style={{ 
              background: 'white',
              padding: '12px 16px',
              borderRadius: 8,
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              borderLeft: '4px solid #f44336',
              minWidth: '140px'
            }}
          >
            <div style={{ fontSize: '1.5rem' }}>⚠️</div>
            <div>
              <h3 style={{ margin: 0, fontSize: '1.5rem', color: '#333' }}>0</h3>
              <p style={{ margin: 0, color: '#666', fontSize: '0.75rem', whiteSpace: 'nowrap' }}>Fora da Rota</p>
            </div>
          </div>
          <div 
            onMouseEnter={() => setShowTooltipProblemaRastreadorCard(true)}
            onMouseLeave={() => setShowTooltipProblemaRastreadorCard(false)}
            style={{ 
              background: 'white',
              padding: '12px 16px',
              borderRadius: 8,
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              borderLeft: '4px solid #9c27b0',
              minWidth: '140px'
            }}
          >
            <div style={{ fontSize: '1.5rem' }}>📡</div>
            <div>
              <h3 style={{ margin: 0, fontSize: '1.5rem', color: '#333' }}>0</h3>
              <p style={{ margin: 0, color: '#666', fontSize: '0.75rem', whiteSpace: 'nowrap' }}>Problema Rastreador</p>
            </div>
          </div>
        </div>
      </div>

      {/* Cards de Estatísticas */}
      <div className="estatisticas-grid">
        <div className="stat-card total">
          <div className="stat-icon">🚛</div>
          <div className="stat-info">
            <h3>{estatisticasFiltradas.totalVeiculos}</h3>
            <p>Total de Veículos</p>
          </div>
        </div>
        <div 
          className="stat-card em-movimento clickable"
          onMouseEnter={() => setShowTooltipEmMovimento(true)}
          onMouseLeave={() => setShowTooltipEmMovimento(false)}
          onClick={gerarRelatorioEmMovimento}
        >
          <div className="stat-icon">▶️</div>
          <div className="stat-info" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
            <div>
              <h3>{estatisticasFiltradas.emMovimento}</h3>
              <p>Em Movimento</p>
            </div>
            <div style={{ marginLeft: 16, textAlign: 'right', fontSize: 18, fontWeight: 500, color: '#222' }}>
              <div>Ida <span style={{ fontWeight: 700 }}>{veiculosFiltrados.filter(v => v.status === 'em_movimento' && v.sentidoRota === 'ida' && !v.problemaRastreador).length}</span></div>
              <div>Volta <span style={{ fontWeight: 700 }}>{veiculosFiltrados.filter(v => v.status === 'em_movimento' && v.sentidoRota === 'volta' && !v.problemaRastreador).length}</span></div>
            </div>
          </div>
          {showTooltipEmMovimento && estatisticasFiltradas.emMovimento > 0 && (
            <div className="tooltip-indicador">
              <div className="tooltip-header">
                <strong>Veículos em Movimento:</strong>
                <small>Clique para baixar relatório</small>
              </div>
              <div className="tooltip-lista">
                {veiculosFiltrados
                  .filter(v => {
                    if (v.status !== 'em_movimento' || v.problemaRastreador) return false;
                    if (v.velocidade > 10 && (!v.poligono || v.poligono === '-')) return true;
                    if (v.velocidade <= 10 && (!v.poligono || v.poligono === '-')) return true;
                    return false;
                  })
                  .map(v => (
                    <div key={v.id} className="tooltip-item">
                      <span className="placa">{v.placa}</span>
                      <span className="info">{v.motorista}</span>
                      <span className="velocidade">{v.velocidade} km/h</span>
                    </div>
                  ))}
              </div>
            </div>
          )}
        </div>
        <div 
          className="stat-card parados clickable"
          onMouseEnter={() => setShowTooltipParados(true)}
          onMouseLeave={() => setShowTooltipParados(false)}
          onClick={gerarRelatorioParados}
        >
          <div className="stat-icon">⏸️</div>
          <div className="stat-info" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
            <div>
              <h3>{estatisticasFiltradas.parados}</h3>
              <p>Parados</p>
            </div>
            <div style={{ marginLeft: 16, textAlign: 'right', fontSize: 16, fontWeight: 500, color: '#222' }}>
              {(() => {
                const parados = veiculosFiltrados.filter(v => v.status === 'parado' && !v.problemaRastreador);
                return <>
              </>;
              })()}
            </div>
          </div>
          {showTooltipParados && estatisticasFiltradas.parados > 0 && (
            <div className="tooltip-indicador">
              <div className="tooltip-header">
                <strong>Veículos Parados:</strong>
                <small>Clique para baixar relatório</small>
              </div>
              <div className="tooltip-lista">
                {veiculosFiltrados
                  .filter(v => v.status === 'parado' && !v.problemaRastreador)
                  .map(v => (
                    <div key={v.id} className="tooltip-item-parado">
                      <div className="parado-header">
                        <span className="placa">{v.placa}</span>
                        <span className="tempo">{v.tempoParado} min</span>
                      </div>
                      <div className="parado-info">
                        <span className="motorista">{v.motorista || 'Sem informação'}</span>
                      </div>
                      <div className="parado-local">
                        <span className="poligono">📍 {v.poligono && v.poligono !== '-' ? v.poligono : (v.localizacao?.endereco || 'Sem informação')}</span>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Timeline Veicular - VW Anchieta → VW Audi SJP */}
      {veiculosTimeline.length > 0 && (
        <div style={{ 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          borderRadius: 12,
          padding: '24px',
          marginBottom: 20,
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
        }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            marginBottom: 20,
            color: 'white'
          }}>
            <div>
              <h3 style={{ margin: 0, fontSize: 20, fontWeight: 600 }}>
                🚛 Timeline: VW Anchieta → VW Audi São José dos Pinhais
              </h3>
              <p style={{ margin: '4px 0 0 0', fontSize: 14, opacity: 0.9 }}>
                {veiculosTimeline.length} veículo(s) em rota • {veiculosTimeline.reduce((sum, v) => sum + v.carrocerias, 0)} carrocerias no total
              </p>
            </div>
            <div style={{ 
              background: 'rgba(255,255,255,0.2)',
              padding: '12px 20px',
              borderRadius: 8,
              textAlign: 'center'
            }}>
              <div style={{ fontSize: 24, fontWeight: 700 }}>
                {carroceriasAcumuladas[1]?.quantidade || 0}
              </div>
              <div style={{ fontSize: 12, opacity: 0.9 }}>
                Carrocerias em 30min
              </div>
            </div>
          </div>

          {/* Gráfico de barras - Carrocerias a cada 30 min */}
          <div style={{ 
            background: 'rgba(255,255,255,0.15)',
            borderRadius: 8,
            padding: 16,
            marginBottom: 20
          }}>
            <h4 style={{ color: 'white', margin: '0 0 12px 0', fontSize: 14 }}>
              📊 Carrocerias chegando a cada 30 minutos
            </h4>
            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', height: 100 }}>
              {carroceriasAcumuladas.map((item, idx) => {
                const maxCarrocerias = Math.max(...carroceriasAcumuladas.map(c => c.quantidade), 1);
                const altura = (item.quantidade / maxCarrocerias) * 100;
                return (
                  <div key={idx} style={{ flex: 1, textAlign: 'center' }}>
                    <div style={{
                      background: 'rgba(255,255,255,0.9)',
                      height: `${altura}%`,
                      minHeight: item.quantidade > 0 ? '20px' : '5px',
                      borderRadius: '4px 4px 0 0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 600,
                      color: '#764ba2',
                      fontSize: 12
                    }}>
                      {item.quantidade > 0 ? item.quantidade : ''}
                    </div>
                    <div style={{ color: 'white', fontSize: 11, marginTop: 4 }}>
                      {item.tempo}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Lista de veículos na timeline */}
          <div style={{ 
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: 12
          }}>
            {veiculosTimeline.map((veiculo, idx) => {
              const veiculoCompleto = veiculos.find(v => v.placa === veiculo.placa);
              return (
              <div key={idx} style={{
                background: 'rgba(255,255,255,0.95)',
                borderRadius: 8,
                padding: 12,
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                borderLeft: veiculo.statusVeiculo === 'parado' ? '4px solid #ff9800' : '4px solid #4caf50',
                cursor: veiculoCompleto ? 'pointer' : 'default'
              }}
              onClick={() => {
                if (veiculoCompleto) {
                  setFiltroPlaca(veiculoCompleto.placa);
                  setVeiculoSelecionado(veiculoCompleto);
                  // Centralizar o mapa no veículo
                  setTimeout(() => {
                    if (mapContainerRef.current && veiculoCompleto.localizacao) {
                      mapContainerRef.current.setView([
                        veiculoCompleto.localizacao.lat,
                        veiculoCompleto.localizacao.lng
                      ], 16);
                    }
                    const mapa = document.querySelector('.mapa-container');
                    if (mapa) {
                      mapa.scrollIntoView({ behavior: 'smooth' });
                    }
                  }, 200);
                }
              }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 16, color: '#333' }}>
                      {veiculo.placa}
                      {veiculo.statusVeiculo === 'parado' && (
                        <span style={{ marginLeft: 8, fontSize: 12, color: '#ff9800', fontWeight: 600 }}>
                          ⏸️ PARADO
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: 12, color: '#666' }}>
                      {veiculo.motoristaNucci || veiculo.motorista}
                    </div>
                    {veiculo.cva && (
                      <div style={{ fontSize: 12, color: '#2196f3', fontWeight: 600, marginTop: 4 }}>
                        📋 CVA: {veiculo.cva}
                      </div>
                    )}
                    {veiculo.poligono && (
                      <div style={{ fontSize: 12, color: '#ff9800', fontWeight: 600, marginTop: 4 }}>
                        📍 Local: {veiculo.poligono}
                      </div>
                    )}
                  </div>
                  <div style={{
                    background: '#4caf50',
                    color: 'white',
                    padding: '4px 8px',
                    borderRadius: 4,
                    fontSize: 12,
                    fontWeight: 600,
                    height: 'fit-content'
                  }}>
                    {veiculo.carrocerias} 🚗
                  </div>
                </div>
                {veiculo.horarioSaidaOrigem && (
                  <div style={{ fontSize: 12, color: '#2196f3', fontWeight: 600, marginBottom: 8, borderLeft: '3px solid #2196f3', paddingLeft: 8 }}>
                    🚀 Saiu da origem: {new Date(veiculo.horarioSaidaOrigem).toLocaleString('pt-BR', { 
                      day: '2-digit', 
                      month: '2-digit', 
                      hour: '2-digit', 
                      minute: '2-digit' 
                    })}
                  </div>
                )}
                <div style={{ fontSize: 12, color: '#555', marginBottom: 4 }}>
                  📍 Faltam <strong>{veiculo.distanciaRestante} km</strong>
                </div>
                {veiculo.statusVeiculo !== 'parado' && (
                  <>
                    <div style={{ fontSize: 12, color: '#555', marginBottom: 4 }}>
                      ⏱️ Tempo estimado: <strong>{veiculo.tempoEstimado}</strong>
                    </div>
                    <div style={{ fontSize: 12, color: '#555', marginBottom: 4 }}>
                      🕐 Hora de chegada: <strong>{veiculo.horaChegada}</strong>
                    </div>
                  </>
                )}
                <div style={{ fontSize: 11, color: '#888' }}>
                  Última atualização: {veiculo.ultimaAtualizacao}
                </div>
              </div>
            );
            })}
          </div>
        </div>
      )}

      <div className="monitoramento-content">
        {/* Mapa */}
        <div className="mapa-container" id="mapa-volkswagen">
          <div className="mapa-header">
            <h3>Mapa de Localização</h3>
            <span className="ultima-atualizacao">
              Última atualização: {estatisticas.ultimaAtualizacao}
            </span>
          </div>
          <MapContainer
            ref={mapContainerRef}
            center={veiculoSelecionado && veiculoSelecionado.localizacao && veiculoSelecionado.localizacao.lat && veiculoSelecionado.localizacao.lng
              ? [veiculoSelecionado.localizacao.lat, veiculoSelecionado.localizacao.lng]
              : [-23.550520, -46.633308]}
            zoom={veiculoSelecionado ? 16 : 12}
            style={{ height: '500px', width: '100%' }}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            {/* Exibir veículos (apenas em movimento e parados) */}
            {veiculosFiltrados
              .filter(v => {
                if (v.problemaRastreador) return false;
                if (v.status === 'parado') return true;
                if (v.status === 'em_movimento') {
                  // Se está em polígono, não mostra como "em movimento"
                  if (v.poligono && v.poligono !== '-') return false;
                  return true;
                }
                return false;
              })
              .map(veiculo => {
              if (!veiculo.localizacao || !veiculo.localizacao.lat || !veiculo.localizacao.lng) {
                return null;
              }
              
              // Calcular hora de chegada para exibir no popup
              const distancia = calcularDistancia(
                veiculo.localizacao.lat, 
                veiculo.localizacao.lng, 
                VW_AUDI_SJP.lat, 
                VW_AUDI_SJP.lng
              );
              const velocidade = veiculo.velocidade || 60;
              const horaChegada = calcularHoraChegada(distancia, velocidade);
              
              
              // Renderizar marcador
              return (
                <Marker
                  key={veiculo.id}
                  position={[veiculo.localizacao.lat, veiculo.localizacao.lng]}
                  icon={createCustomIcon(veiculo)}
                >
                  <Popup>
                    <div>
                      <h3>{veiculo.placa}</h3>
                      <p>Velocidade: {veiculo.velocidade} km/h</p>
                      <p>Chegada: {horaChegada}</p>
                    </div>
                  </Popup>
                </Marker>
              );
            })}
            {/* Exibir rotas selecionadas com trajeto detalhado */}
            {rotasSelecionadas.map(rotaId => {
              const rota = rotas.find(r => r.id === rotaId);
              if (!rota) return null;
              const polyline = rotasDetalhadas[rotaId];
              if (!polyline || polyline.length < 2) return null;
              return (
                <Polyline
                  key={rota.id}
                  positions={polyline}
                  color={rota.cor || 'blue'}
                  weight={5}
                  opacity={0.8}
                >
                  <Popup>
                    <div className="marker-popup">
                      <h4>{rota.nome}</h4>
                      <p><strong>Origem:</strong> {rota.origem}</p>
                      <p><strong>Destino:</strong> {rota.destino}</p>
                      <p><strong>Distância:</strong> {rota.distancia} km</p>
                      <p><strong>Tempo:</strong> {rota.tempoEstimado}h</p>
                    </div>
                  </Popup>
                </Polyline>
              );
            })}
            
            {/* Exibir polígonos cadastrados */}
            {poligonos.map(poligono => {
              const positions: [number, number][] = poligono.coordenadas.map(p => [p.lat, p.lng]);
              return (
                <LeafletPolygon
                  key={poligono.id}
                  positions={positions}
                  pathOptions={{
                    color: poligono.cor,
                    fillColor: poligono.cor,
                    fillOpacity: 0.2,
                    weight: 3
                  }}
                >
                  <Popup>
                    <div className="marker-popup">
                      <h4>{poligono.nome}</h4>
                      <p><strong>Tipo:</strong> {poligono.tipo}</p>
                      {poligono.descricao && <p><strong>Descrição:</strong> {poligono.descricao}</p>}
                      <p><strong>Pontos:</strong> {poligono.coordenadas.length}</p>
                    </div>
                  </Popup>
                </LeafletPolygon>
              );
            })}
            
          </MapContainer>
        </div>

        {/* Lista de Veículos */}
        <div className="veiculos-container">
          {filtroPlaca && (
            <div style={{ margin: '8px 0', textAlign: 'right' }}>
              <button onClick={() => { setFiltroPlaca(''); setVeiculoSelecionado(null); }} style={{ background: '#eee', border: 'none', borderRadius: 4, padding: '4px 12px', cursor: 'pointer' }}>
                Limpar filtro
              </button>
            </div>
          )}
          <div className="veiculos-header">
            <h3>Veículos</h3>
            <button 
              className="btn-expandir" 
              onClick={() => setShowModalVeiculos(true)}
              title="Expandir"
            >
              <span style={{ fontSize: '1.2rem' }}>⛶</span>
            </button>
          </div>
          <div className="veiculos-lista">
            {veiculosFiltrados
              .filter(v => {
                if (v.problemaRastreador) return false;
                if (v.status === 'parado') return true;
                if (v.status === 'em_movimento') {
                  // Se está em polígono, não mostra como "em movimento"
                  if (v.poligono && v.poligono !== '-') return false;
                  return true;
                }
                return false;
              })
              .map(veiculo => (
              <div
                key={veiculo.id}
                className={`veiculo-card ${veiculoSelecionado?.id === veiculo.id ? 'selected' : ''}`}
                onClick={() => setVeiculoSelecionado(veiculo)}
              >
                <div className="veiculo-header">
                  <span className="veiculo-placa">{veiculo.placa}</span>
                  <span 
                    className="veiculo-status" 
                    style={{ backgroundColor: getStatusColor(veiculo.status) }}
                  >
                    {getStatusLabel(veiculo.status)}
                  </span>
                </div>
                <div className="veiculo-info">
                  <p><strong>Motorista:</strong> {dadosNucci[veiculo.placa]?.motoristaNucci || veiculo.motorista}</p>
                  {dadosNucci[veiculo.placa]?.cva && (
                    <p style={{ color: '#2196f3', fontWeight: 600 }}>
                      <strong>📋 CVA:</strong> {dadosNucci[veiculo.placa].cva}
                    </p>
                  )}
                  <p><strong>Velocidade:</strong> {veiculo.velocidade} km/h</p>
                  <p><strong>Operação:</strong> {veiculo.operacao}</p>
                  <p><strong>Localização:</strong> {veiculo.poligono}</p>
                  {veiculo.sentidoRota && veiculo.sentidoRota !== 'desconhecido' && (
                    <p>
                      <strong>Sentido:</strong> 
                      <span style={{ 
                        marginLeft: 6,
                        padding: '2px 8px',
                        borderRadius: 4,
                        fontSize: '0.85em',
                        fontWeight: 600,
                        backgroundColor: veiculo.sentidoRota === 'ida' ? '#4caf50' : '#2196f3',
                        color: 'white'
                      }}>
                        {veiculo.sentidoRota === 'ida' ? '➡️ Ida' : '⬅️ Volta'} ({veiculo.confiancaSentido}%)
                      </span>
                    </p>
                  )}
                  <p><strong>Última atualização:</strong> {veiculo.ultimaAtualizacao}</p>
                  {veiculo.ultimaComunicacao && (
                    <p><strong>Última comunicação:</strong> {veiculo.ultimaComunicacao}</p>
                  )}
                </div>
                <div className="veiculo-localizacao">
                  📍 {veiculo.localizacao?.endereco || 'Localização não disponível'}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Modal Cadastro de Rota */}
      {showCadastroRota && (
        <div className="modal-overlay" onClick={() => setShowCadastroRota(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{rotaEmEdicao ? 'Editar Rota' : 'Cadastrar Nova Rota'}</h3>
              <button className="modal-close" onClick={() => {
                setShowCadastroRota(false);
                setRotaEmEdicao(null);
                setNovaRota({ nome: '', origem: '', destino: '', pontosParada: '', distancia: '', tempoEstimado: '', cor: '#2196f3', pontos: [{ lat: '', lng: '', endereco: '' }] });
              }}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Nome da Rota:</label>
                <input type="text" value={novaRota.nome} onChange={e => setNovaRota({...novaRota, nome: e.target.value})} placeholder="Ex: SP - RJ" />
              </div>
              <div className="form-group">
                <label>Origem:</label>
                <input type="text" value={novaRota.origem} onChange={e => setNovaRota({...novaRota, origem: e.target.value})} placeholder="Endereço de origem" />
              </div>
              <div className="form-group">
                <label>Destino:</label>
                <input type="text" value={novaRota.destino} onChange={e => setNovaRota({...novaRota, destino: e.target.value})} placeholder="Endereço de destino" />
              </div>
              <div className="form-group">
                <label>Pontos de Parada (opcional):</label>
                <textarea value={novaRota.pontosParada} onChange={e => setNovaRota({...novaRota, pontosParada: e.target.value})} placeholder="Lista de pontos de parada" rows={3}></textarea>
              </div>
              <div className="form-group">
                <label>Pontos da Rota (Latitude/Longitude):</label>
                {novaRota.pontos.map((ponto, idx) => (
                  <div key={idx} style={{ display: 'flex', gap: 8, marginBottom: 4, alignItems: 'center' }}>
                    <input
                      type="text"
                      placeholder="Endereço ou referência"
                      value={ponto.endereco || ''}
                      onChange={e => {
                        const novosPontos = [...novaRota.pontos];
                        novosPontos[idx].endereco = e.target.value;
                        setNovaRota({ ...novaRota, pontos: novosPontos });
                      }}
                      onBlur={async e => {
                        const endereco = e.target.value;
                        if (endereco && (!ponto.lat || !ponto.lng)) {
                          const coords = await buscarLatLngPorEndereco(endereco);
                          const novosPontos = [...novaRota.pontos];
                          novosPontos[idx].lat = coords.lat;
                          novosPontos[idx].lng = coords.lng;
                          setNovaRota({ ...novaRota, pontos: novosPontos });
                        }
                      }}
                      style={{ width: 220 }}
                    />
                    <input
                      type="text"
                      placeholder="Latitude"
                      value={ponto.lat}
                      onChange={e => {
                        const novosPontos = [...novaRota.pontos];
                        novosPontos[idx].lat = e.target.value;
                        setNovaRota({ ...novaRota, pontos: novosPontos });
                      }}
                      style={{ width: 100 }}
                    />
                    <input
                      type="text"
                      placeholder="Longitude"
                      value={ponto.lng}
                      onChange={e => {
                        const novosPontos = [...novaRota.pontos];
                        novosPontos[idx].lng = e.target.value;
                        setNovaRota({ ...novaRota, pontos: novosPontos });
                      }}
                      style={{ width: 100 }}
                    />
                    <button
                      type="button"
                      style={{ background: '#e57373', color: '#fff', border: 'none', borderRadius: 4, padding: '0 8px', cursor: 'pointer' }}
                      onClick={() => {
                        const novosPontos = novaRota.pontos.filter((_, i) => i !== idx);
                        setNovaRota({ ...novaRota, pontos: novosPontos.length ? novosPontos : [{ lat: '', lng: '', endereco: '' }] });
                      }}
                      disabled={novaRota.pontos.length === 1}
                    >
                      Remover
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  style={{ background: '#2196f3', color: '#fff', border: 'none', borderRadius: 4, padding: '0 12px', marginTop: 4, cursor: 'pointer' }}
                  onClick={() => setNovaRota({ ...novaRota, pontos: [...novaRota.pontos, { lat: '', lng: '', endereco: '' }] })}
                >
                  + Adicionar Ponto
                </button>
              </div>
              <div className="form-group">
                <label>Distância Estimada (km):</label>
                <input type="number" value={novaRota.distancia} onChange={e => setNovaRota({...novaRota, distancia: e.target.value})} placeholder="0" />
              </div>
              <div className="form-group">
                <label>Tempo Estimado (horas):</label>
                <input type="number" value={novaRota.tempoEstimado} onChange={e => setNovaRota({...novaRota, tempoEstimado: e.target.value})} placeholder="0" />
              </div>
              <div className="form-group">
                <label>Cor da Rota:</label>
                <input
                  type="color"
                  value={novaRota.cor || '#2196f3'}
                  onChange={e => setNovaRota({ ...novaRota, cor: e.target.value })}
                  style={{ width: 48, height: 32, border: 'none', background: 'none', cursor: 'pointer' }}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => {
                setShowCadastroRota(false);
                setRotaEmEdicao(null);
                setNovaRota({ nome: '', origem: '', destino: '', pontosParada: '', distancia: '', tempoEstimado: '', cor: '#2196f3', pontos: [{ lat: '', lng: '', endereco: '' }] });
              }}>
                Cancelar
              </button>
              <button className="btn-primary" onClick={async () => {
                try {
                  const dadosRota = {
                    ...novaRota,
                    cor: novaRota.cor || '#2196f3',
                    pontos: novaRota.pontos
                      .filter(p => p.lat && p.lng)
                      .map(p => ({ lat: parseFloat(p.lat), lng: parseFloat(p.lng) }))
                      .filter(p => !isNaN(p.lat) && !isNaN(p.lng)),
                  };
                  
                  if (rotaEmEdicao) {
                    await axios.put(`/api/monitoramento/rotas/${rotaEmEdicao}`, dadosRota);
                  } else {
                    await axios.post('/api/monitoramento/rotas', dadosRota);
                  }
                  
                  setShowCadastroRota(false);
                  setRotaEmEdicao(null);
                  setNovaRota({ nome: '', origem: '', destino: '', pontosParada: '', distancia: '', tempoEstimado: '', cor: '#2196f3', pontos: [{ lat: '', lng: '', endereco: '' }] });
                  fetchDados();
                } catch (err) {
                  console.error('Erro ao salvar rota:', err);
                  alert(rotaEmEdicao ? 'Erro ao atualizar rota!' : 'Erro ao cadastrar rota!');
                }
              }}>
                Salvar Rota
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Cadastro de Operação */}
      {showCadastroOperacao && (
        <div className="modal-overlay" onClick={() => setShowCadastroOperacao(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 700 }}>
            <div className="modal-header">
              <h3>{operacaoEmEdicao ? 'Editar Operação' : 'Cadastrar Operação'}</h3>
              <button className="close-btn" onClick={() => setShowCadastroOperacao(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Nome da Operação:</label>
                <input 
                  type="text" 
                  value={novaOperacao.nome} 
                  onChange={e => setNovaOperacao({...novaOperacao, nome: e.target.value})} 
                  placeholder="Ex: Volks, Brose, etc." 
                />
              </div>
              <div className="form-group">
                <label>Descrição (opcional):</label>
                <textarea 
                  value={novaOperacao.descricao} 
                  onChange={e => setNovaOperacao({...novaOperacao, descricao: e.target.value})} 
                  placeholder="Descrição da operação..."
                  rows={3}
                  style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ddd' }}
                />
              </div>
              
              {/* Novos campos de Polígonos */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div className="form-group">
                  <label style={{ fontSize: 16, fontWeight: 600, color: '#333', marginBottom: 8, display: 'block' }}>
                    📍 Polígono Origem
                  </label>
                  <select
                    value={novaOperacao.poligonoOrigem || ''}
                    onChange={e => setNovaOperacao({...novaOperacao, poligonoOrigem: e.target.value ? Number(e.target.value) : undefined})}
                    style={{ 
                      width: '100%', 
                      padding: '10px 14px', 
                      borderRadius: 6, 
                      border: '2px solid #e0e0e0',
                      fontSize: 14,
                      outline: 'none',
                      cursor: 'pointer'
                    }}
                  >
                    <option value="">Selecione o polígono de origem</option>
                    {poligonos
                      .filter(p => p.ativa !== false)
                      .map(poligono => (
                        <option key={poligono.id} value={poligono.id}>
                          {poligono.nome} ({poligono.tipo})
                        </option>
                      ))
                    }
                  </select>
                </div>
                
                <div className="form-group">
                  <label style={{ fontSize: 16, fontWeight: 600, color: '#333', marginBottom: 8, display: 'block' }}>
                    🎯 Polígono Destino
                  </label>
                  <select
                    value={novaOperacao.poligonoDestino || ''}
                    onChange={e => setNovaOperacao({...novaOperacao, poligonoDestino: e.target.value ? Number(e.target.value) : undefined})}
                    style={{ 
                      width: '100%', 
                      padding: '10px 14px', 
                      borderRadius: 6, 
                      border: '2px solid #e0e0e0',
                      fontSize: 14,
                      outline: 'none',
                      cursor: 'pointer'
                    }}
                  >
                    <option value="">Selecione o polígono de destino</option>
                    {poligonos
                      .filter(p => p.ativa !== false && p.id !== novaOperacao.poligonoOrigem)
                      .map(poligono => (
                        <option key={poligono.id} value={poligono.id}>
                          {poligono.nome} ({poligono.tipo})
                        </option>
                      ))
                    }
                  </select>
                </div>
              </div>
              
              <div className="form-group">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                  <label style={{ fontSize: 16, fontWeight: 600, color: '#333', margin: 0 }}>🗺️ Rotas da Operação</label>
                  <span style={{ fontSize: 14, color: '#2196f3', fontWeight: 500 }}>
                    {novaOperacao.rotas.length} {novaOperacao.rotas.length === 1 ? 'selecionada' : 'selecionadas'}
                  </span>
                </div>
                <input 
                  type="text" 
                  placeholder="🔍 Buscar rota por nome, origem ou destino..."
                  value={filtroRotasModal}
                  onChange={e => setFiltroRotasModal(e.target.value)}
                  style={{ 
                    padding: '10px 14px', 
                    border: '2px solid #e0e0e0', 
                    borderRadius: 6, 
                    width: '100%', 
                    fontSize: 14,
                    marginBottom: 12,
                    outline: 'none',
                    transition: 'border-color 0.2s'
                  }}
                  onFocus={e => e.target.style.borderColor = '#2196f3'}
                  onBlur={e => e.target.style.borderColor = '#e0e0e0'}
                />
                <div style={{ 
                  maxHeight: 280, 
                  overflowY: 'auto', 
                  border: '2px solid #e0e0e0', 
                  borderRadius: 8, 
                  padding: 12, 
                  background: '#f8f9fa' 
                }}>
                  {rotas.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
                      <div style={{ fontSize: 48, marginBottom: 12 }}>🗺️</div>
                      <div style={{ fontSize: 14 }}>Nenhuma rota disponível</div>
                    </div>
                  ) : rotas
                      .filter(rota => 
                        filtroRotasModal === '' ||
                        rota.nome.toLowerCase().includes(filtroRotasModal.toLowerCase()) ||
                        rota.origem.toLowerCase().includes(filtroRotasModal.toLowerCase()) ||
                        rota.destino.toLowerCase().includes(filtroRotasModal.toLowerCase())
                      )
                      .map(rota => {
                        const isSelected = novaOperacao.rotas.includes(rota.id);
                        return (
                          <label
                            key={rota.id}
                            htmlFor={`rota-${rota.id}`}
                            style={{ 
                              display: 'block',
                              background: isSelected ? '#e3f2fd' : 'white',
                              padding: '14px 16px',
                              marginBottom: 10,
                              borderRadius: 8,
                              border: isSelected ? '2px solid #2196f3' : '2px solid #e0e0e0',
                              cursor: 'pointer',
                              transition: 'all 0.2s',
                              boxShadow: isSelected ? '0 2px 8px rgba(33, 150, 243, 0.2)' : '0 1px 3px rgba(0,0,0,0.05)'
                            }}
                            onMouseEnter={e => {
                              if (!isSelected) {
                                e.currentTarget.style.borderColor = '#2196f3';
                                e.currentTarget.style.boxShadow = '0 2px 8px rgba(33, 150, 243, 0.15)';
                              }
                            }}
                            onMouseLeave={e => {
                              if (!isSelected) {
                                e.currentTarget.style.borderColor = '#e0e0e0';
                                e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.05)';
                              }
                            }}
                          >
                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                              <input 
                                type="checkbox" 
                                id={`rota-${rota.id}`}
                                checked={isSelected}
                                onChange={() => {
                                  setNovaOperacao(prev => ({
                                    ...prev,
                                    rotas: prev.rotas.includes(rota.id) 
                                      ? prev.rotas.filter(id => id !== rota.id)
                                      : [...prev.rotas, rota.id]
                                  }));
                                }}
                                style={{ marginTop: 4, width: 18, height: 18, cursor: 'pointer' }}
                              />
                              <div style={{ flex: 1 }}>
                                <div style={{ 
                                  fontSize: 15, 
                                  fontWeight: 600, 
                                  color: isSelected ? '#1976d2' : '#333',
                                  marginBottom: 6
                                }}>
                                  {rota.nome}
                                </div>
                                <div style={{ 
                                  fontSize: 13, 
                                  color: '#666',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 6
                                }}>
                                  <span style={{ color: '#4caf50', fontWeight: 600 }}>📍</span>
                                  <span>{rota.origem}</span>
                                  <span style={{ color: '#2196f3', fontWeight: 600 }}>→</span>
                                  <span>{rota.destino}</span>
                                </div>
                              </div>
                            </div>
                          </label>
                        );
                      })
                  }
                </div>
              </div>
              <div className="form-group">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                  <label style={{ fontSize: 16, fontWeight: 600, color: '#333', margin: 0 }}>🚛 Veículos da Operação</label>
                  <span style={{ fontSize: 14, color: '#2196f3', fontWeight: 500 }}>
                    {novaOperacao.veiculos.length} {novaOperacao.veiculos.length === 1 ? 'selecionado' : 'selecionados'}
                  </span>
                </div>
                <input 
                  type="text" 
                  placeholder="🔍 Buscar veículo por placa ou motorista..."
                  value={filtroVeiculosModal}
                  onChange={e => setFiltroVeiculosModal(e.target.value)}
                  style={{ 
                    padding: '10px 14px', 
                    border: '2px solid #e0e0e0', 
                    borderRadius: 6, 
                    width: '100%', 
                    fontSize: 14,
                    marginBottom: 12,
                    outline: 'none',
                    transition: 'border-color 0.2s'
                  }}
                  onFocus={e => e.target.style.borderColor = '#2196f3'}
                  onBlur={e => e.target.style.borderColor = '#e0e0e0'}
                />
                <div style={{ 
                  maxHeight: 280, 
                  overflowY: 'auto', 
                  border: '2px solid #e0e0e0', 
                  borderRadius: 8, 
                  padding: 12, 
                  background: '#f8f9fa' 
                }}>
                  {veiculos.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
                      <div style={{ fontSize: 48, marginBottom: 12 }}>🚛</div>
                      <div style={{ fontSize: 14 }}>Nenhum veículo disponível</div>
                    </div>
                  ) : veiculos
                      .filter(veiculo => 
                        filtroVeiculosModal === '' ||
                        veiculo.placa.toLowerCase().includes(filtroVeiculosModal.toLowerCase()) ||
                        (veiculo.motorista && veiculo.motorista.toLowerCase().includes(filtroVeiculosModal.toLowerCase()))
                      )
                      .map(veiculo => {
                        const isSelected = novaOperacao.veiculos.includes(veiculo.placa);
                        return (
                          <label
                            key={veiculo.placa}
                            htmlFor={`veiculo-${veiculo.placa}`}
                            style={{ 
                              display: 'block',
                              background: isSelected ? '#e3f2fd' : 'white',
                              padding: '14px 16px',
                              marginBottom: 10,
                              borderRadius: 8,
                              border: isSelected ? '2px solid #2196f3' : '2px solid #e0e0e0',
                              cursor: 'pointer',
                              transition: 'all 0.2s',
                              boxShadow: isSelected ? '0 2px 8px rgba(33, 150, 243, 0.2)' : '0 1px 3px rgba(0,0,0,0.05)'
                            }}
                            onMouseEnter={e => {
                              if (!isSelected) {
                                e.currentTarget.style.borderColor = '#2196f3';
                                e.currentTarget.style.boxShadow = '0 2px 8px rgba(33, 150, 243, 0.15)';
                              }
                            }}
                            onMouseLeave={e => {
                              if (!isSelected) {
                                e.currentTarget.style.borderColor = '#e0e0e0';
                                e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.05)';
                              }
                            }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                              <input 
                                type="checkbox" 
                                id={`veiculo-${veiculo.placa}`}
                                checked={isSelected}
                                onChange={() => {
                                  setNovaOperacao(prev => ({
                                    ...prev,
                                    veiculos: prev.veiculos.includes(veiculo.placa) 
                                      ? prev.veiculos.filter(placa => placa !== veiculo.placa)
                                      : [...prev.veiculos, veiculo.placa]
                                  }));
                                }}
                                style={{ width: 18, height: 18, cursor: 'pointer' }}
                              />
                              <div style={{ flex: 1 }}>
                                <div style={{ 
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 10,
                                  marginBottom: 4
                                }}>
                                  <span style={{
                                    background: isSelected ? '#1976d2' : '#2196f3',
                                    color: 'white',
                                    padding: '4px 12px',
                                    borderRadius: 4,
                                    fontSize: 14,
                                    fontWeight: 600,
                                    letterSpacing: '0.5px'
                                  }}>
                                    {veiculo.placa}
                                  </span>
                                </div>
                                <div style={{ 
                                  fontSize: 13, 
                                  color: '#666',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 6
                                }}>
                                  <span>👤</span>
                                  <span>{veiculo.motorista || 'Sem informação'}</span>
                                </div>
                              </div>
                            </div>
                          </label>
                        );
                      })
                  }
                </div>
              </div>
              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                  <input 
                    type="checkbox" 
                    checked={novaOperacao.ativa}
                    onChange={() => setNovaOperacao({...novaOperacao, ativa: !novaOperacao.ativa})}
                    style={{ marginRight: 8 }}
                  />
                  Operação Ativa
                </label>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => {
                setShowCadastroOperacao(false);
                setOperacaoEmEdicao(null);
                setNovaOperacao({ nome: '', descricao: '', rotas: [], veiculos: [], ativa: true });
                setFiltroRotasModal('');
                setFiltroVeiculosModal('');
              }}>
                Cancelar
              </button>
              <button className="btn-primary" onClick={async () => {
                try {
                  if (!novaOperacao.nome.trim()) {
                    alert('Por favor, informe o nome da operação!');
                    return;
                  }
                  
                  // Sanitizar dados antes de enviar
                  const dadosOperacao = {
                    nome: novaOperacao.nome.trim(),
                    descricao: novaOperacao.descricao?.trim() || '',
                    rotas: Array.isArray(novaOperacao.rotas) ? novaOperacao.rotas : [],
                    veiculos: Array.isArray(novaOperacao.veiculos) ? novaOperacao.veiculos : [],
                    ativa: Boolean(novaOperacao.ativa)
                  };
                  
                  if (operacaoEmEdicao) {
                    await axios.put(`/api/monitoramento/operacoes/${operacaoEmEdicao}`, dadosOperacao);
                    alert('Operação atualizada com sucesso!');
                  } else {
                    await axios.post('/api/monitoramento/operacoes', dadosOperacao);
                    alert('Operação cadastrada com sucesso!');
                  }
                  
                  setShowCadastroOperacao(false);
                  setOperacaoEmEdicao(null);
                  setNovaOperacao({ nome: '', descricao: '', rotas: [], veiculos: [], ativa: true });
                  setFiltroRotasModal('');
                  setFiltroVeiculosModal('');
                  fetchDados();
                } catch (err) {
                  console.error('Erro ao salvar operação:', err);
                  console.error('Dados que causaram erro:', novaOperacao);
                  alert(operacaoEmEdicao ? 'Erro ao atualizar operação!' : 'Erro ao cadastrar operação!');
                }
              }}>
                {operacaoEmEdicao ? 'Atualizar Operação' : 'Salvar Operação'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Lista/Gerenciar Operações */}
      {showListaOperacoes && (
        <div className="modal-overlay" onClick={() => setShowListaOperacoes(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 900, maxHeight: '80vh' }}>
            <div className="modal-header">
              <h3>🏭 Gerenciar Operações</h3>
              <button className="close-btn" onClick={() => setShowListaOperacoes(false)}>×</button>
            </div>
            <div className="modal-body" style={{ overflowY: 'auto', maxHeight: 'calc(80vh - 150px)' }}>
              {operacoes.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 60, color: '#999' }}>
                  <div style={{ fontSize: 64, marginBottom: 16 }}>🏭</div>
                  <div style={{ fontSize: 16 }}>Nenhuma operação cadastrada</div>
                  <button 
                    className="btn-primary" 
                    onClick={() => {
                      setShowListaOperacoes(false);
                      setShowCadastroOperacao(true);
                    }}
                    style={{ marginTop: 20 }}
                  >
                    + Cadastrar Primeira Operação
                  </button>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  {operacoes.map(operacao => (
                    <div 
                      key={operacao.id}
                      style={{
                        border: '2px solid #e0e0e0',
                        borderRadius: 8,
                        padding: 20,
                        background: operacao.ativa ? 'white' : '#f5f5f5'
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                            <h4 style={{ margin: 0, fontSize: 18, color: '#333' }}>{operacao.nome}</h4>
                            {operacao.ativa ? (
                              <span style={{ background: '#4caf50', color: 'white', padding: '2px 8px', borderRadius: 4, fontSize: 12, fontWeight: 600 }}>
                                ✓ Ativa
                              </span>
                            ) : (
                              <span style={{ background: '#999', color: 'white', padding: '2px 8px', borderRadius: 4, fontSize: 12, fontWeight: 600 }}>
                                ✕ Inativa
                              </span>
                            )}
                          </div>
                          {operacao.descricao && (
                            <p style={{ margin: '8px 0', color: '#666', fontSize: 14 }}>{operacao.descricao}</p>
                          )}
                        </div>
                        <div style={{ display: 'flex', gap: 8 }}>
                          <button 
                            className="btn-secondary"
                            onClick={() => {
                              setOperacaoEmEdicao(operacao.id!);
                              setNovaOperacao({
                                nome: operacao.nome,
                                descricao: operacao.descricao || '',
                                rotas: operacao.rotas,
                                veiculos: operacao.veiculos,
                                poligonoOrigem: operacao.poligonoOrigem,
                                poligonoDestino: operacao.poligonoDestino,
                                ativa: operacao.ativa
                              });
                              setShowListaOperacoes(false);
                              setShowCadastroOperacao(true);
                            }}
                            style={{ fontSize: 12, padding: '6px 12px' }}
                          >
                            ✏️ Editar
                          </button>
                          <button 
                            className="btn-secondary"
                            onClick={async () => {
                              if (window.confirm(`Deseja realmente excluir a operação "${operacao.nome}"?`)) {
                                try {
                                  await axios.delete(`/api/monitoramento/operacoes/${operacao.id}`);
                                  fetchDados();
                                  alert('Operação excluída com sucesso!');
                                } catch (err) {
                                  alert('Erro ao excluir operação!');
                                }
                              }
                            }}
                            style={{ fontSize: 12, padding: '6px 12px', background: '#f44336', color: 'white' }}
                          >
                            🗑️ Excluir
                          </button>
                        </div>
                      </div>
                      
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginTop: 16 }}>
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 600, color: '#666', marginBottom: 8 }}>
                            🗺️ Rotas ({operacao.rotas.length})
                          </div>
                          <div style={{ background: '#f8f9fa', padding: 10, borderRadius: 6, maxHeight: 120, overflowY: 'auto' }}>
                            {operacao.rotas.length === 0 ? (
                              <span style={{ fontSize: 12, color: '#999' }}>Nenhuma rota vinculada</span>
                            ) : (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                {operacao.rotas.map(rotaId => {
                                  const rota = rotas.find(r => r.id === rotaId);
                                  return rota ? (
                                    <div key={rotaId} style={{ fontSize: 12, color: '#333', padding: '4px 8px', background: 'white', borderRadius: 4 }}>
                                      {rota.nome}
                                    </div>
                                  ) : null;
                                })}
                              </div>
                            )}
                          </div>
                        </div>
                        
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 600, color: '#666', marginBottom: 8 }}>
                            🚛 Veículos ({operacao.veiculos.length})
                          </div>
                          <div style={{ background: '#f8f9fa', padding: 10, borderRadius: 6, maxHeight: 120, overflowY: 'auto' }}>
                            {operacao.veiculos.length === 0 ? (
                              <span style={{ fontSize: 12, color: '#999' }}>Nenhum veículo vinculado</span>
                            ) : (
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                {operacao.veiculos.map(placa => {
                                  const veiculo = veiculos.find(v => v.placa === placa);
                                  return veiculo ? (
                                    <span key={placa} style={{ 
                                      fontSize: 11, 
                                      background: '#2196f3', 
                                      color: 'white', 
                                      padding: '3px 8px', 
                                      borderRadius: 4,
                                      fontWeight: 600
                                    }}>
                                      {veiculo.placa}
                                    </span>
                                  ) : null;
                                })}
                              </div>
                            )}
                          </div>
                        </div>
                        
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 600, color: '#666', marginBottom: 8 }}>
                            📍 Polígonos Origem/Destino
                          </div>
                          <div style={{ background: '#f8f9fa', padding: 10, borderRadius: 6, minHeight: 80 }}>
                            {!operacao.poligonoOrigem && !operacao.poligonoDestino ? (
                              <span style={{ fontSize: 12, color: '#999' }}>Nenhum polígono vinculado</span>
                            ) : (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                {operacao.poligonoOrigem && (
                                  <div style={{ fontSize: 12 }}>
                                    <span style={{ color: '#4caf50', fontWeight: 600 }}>📍 Origem:</span>{' '}
                                    <span style={{ background: 'white', padding: '2px 8px', borderRadius: 4 }}>
                                      {poligonos.find(p => p.id === operacao.poligonoOrigem)?.nome || `ID ${operacao.poligonoOrigem}`}
                                    </span>
                                  </div>
                                )}
                                {operacao.poligonoDestino && (
                                  <div style={{ fontSize: 12 }}>
                                    <span style={{ color: '#f44336', fontWeight: 600 }}>🎯 Destino:</span>{' '}
                                    <span style={{ background: 'white', padding: '2px 8px', borderRadius: 4 }}>
                                      {poligonos.find(p => p.id === operacao.poligonoDestino)?.nome || `ID ${operacao.poligonoDestino}`}
                                    </span>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowListaOperacoes(false)}>
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Veículos Expandido */}
      {showModalVeiculos && (
        <div className="modal-overlay" onClick={() => setShowModalVeiculos(false)}>
          <div className="modal-content-large" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '95vw', maxHeight: '95vh', width: '95vw' }}>
            <div className="modal-header">
              <h2>🚛 Veículos em Operação - Volkswagen</h2>
              <button className="btn-close" onClick={() => setShowModalVeiculos(false)}>✕</button>
            </div>
            <div className="modal-body" style={{ maxHeight: 'calc(95vh - 120px)', overflowY: 'auto' }}>
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', 
                gap: '1.5rem',
                padding: '1rem'
              }}>
                {veiculosFiltrados
                  .filter(v => {
                    if (v.problemaRastreador) return false;
                    if (v.status === 'parado') return true;
                    if (v.status === 'em_movimento') {
                      if (v.poligono && v.poligono !== '-') return false;
                      return true;
                    }
                    return false;
                  })
                  .map(veiculo => (
                    <div
                      key={veiculo.id}
                      className={`veiculo-card ${veiculoSelecionado?.id === veiculo.id ? 'selected' : ''}`}
                      onClick={() => {
                        setVeiculoSelecionado(veiculo);
                        setShowModalVeiculos(false);
                      }}
                      style={{ cursor: 'pointer', transition: 'all 0.3s' }}
                    >
                      <div className="veiculo-header">
                        <span className="veiculo-placa">{veiculo.placa}</span>
                        <span 
                          className="veiculo-status" 
                          style={{ backgroundColor: getStatusColor(veiculo.status) }}
                        >
                          {getStatusLabel(veiculo.status)}
                        </span>
                      </div>
                      <div className="veiculo-info">
                        <p><strong>Motorista:</strong> {dadosNucci[veiculo.placa]?.motoristaNucci || veiculo.motorista}</p>
                        {dadosNucci[veiculo.placa]?.cva && (
                          <p style={{ color: '#2196f3', fontWeight: 600 }}>
                            <strong>📋 CVA:</strong> {dadosNucci[veiculo.placa].cva}
                          </p>
                        )}
                        <p><strong>Velocidade:</strong> {veiculo.velocidade} km/h</p>
                        <p><strong>Operação:</strong> {veiculo.operacao}</p>
                        <p><strong>Localização:</strong> {veiculo.poligono}</p>
                        {veiculo.sentidoRota && veiculo.sentidoRota !== 'desconhecido' && (
                          <p>
                            <strong>Sentido:</strong> 
                            <span style={{ 
                              marginLeft: 6,
                              padding: '2px 8px',
                              borderRadius: 4,
                              fontSize: '0.85em',
                              fontWeight: 600,
                              backgroundColor: veiculo.sentidoRota === 'ida' ? '#4caf50' : '#2196f3',
                              color: 'white'
                            }}>
                              {veiculo.sentidoRota === 'ida' ? '➡️ Ida' : '⬅️ Volta'} ({veiculo.confiancaSentido}%)
                            </span>
                          </p>
                        )}
                        <p><strong>Última atualização:</strong> {veiculo.ultimaAtualizacao}</p>
                        {veiculo.localizacao?.endereco && (
                          <p style={{ fontSize: '0.85em', color: '#666', marginTop: '0.5rem' }}>
                            📍 {veiculo.localizacao.endereco}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
              </div>
              {veiculosFiltrados.filter(v => {
                if (v.problemaRastreador) return false;
                if (v.status === 'parado') return true;
                if (v.status === 'em_movimento') {
                  if (v.poligono && v.poligono !== '-') return false;
                  return true;
                }
                return false;
              }).length === 0 && (
                <div style={{ textAlign: 'center', padding: '3rem', color: '#999' }}>
                  <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>🚛</div>
                  <h3>Nenhum veículo em operação no momento</h3>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowModalVeiculos(false)}>
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MonitoramentoVolkswagenModule;